import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';

import { mysteryBundleReveal, revealMysteryKey } from 'Redux/ducks/orders';
import imgixConfig from 'Config/imgix';
import PinataReveal from 'Components/Order/KeyReveals/Pinata/PinataReveal';
import PinataIntro from 'Components/Order/KeyReveals/Pinata/PinataIntro';
import ScratchCardReveal from 'Components/Order/KeyReveals/ScratchCard/ScratchCardReveal';
import ScratchCardIntro from 'Components/Order/KeyReveals/ScratchCard/ScratchCardIntro';
import StandardBundleIntro from 'Components/Order/KeyReveals/StandardBundle/StandardBundleIntro';
import MysteryRevealGames from 'Components/Order/KeyReveals/MysteryRevealGames';
import WheelIntro from 'Components/Order/KeyReveals/Wheel/WheelIntro';
import WheelReveal from 'Components/Order/KeyReveals/Wheel/WheelReveal';
import WheelIntroJingleJam from 'Components/Order/KeyReveals/Wheel/WheelIntroJingleJam';
import WheelRevealJingleJam from 'Components/Order/KeyReveals/Wheel/WheelRevealJingleJam';
import GiftBoxIntro from 'Components/Order/KeyReveals/GiftBox/GiftBoxIntro';
import GiftBoxReveal from 'Components/Order/KeyReveals/GiftBox/GiftBoxReveal';
import EnvelopeIntro from 'Components/Order/KeyReveals/Envelope/EnvelopeIntro';
import EnvelopeReveal from 'Components/Order/KeyReveals/Envelope/EnvelopeReveal';
import EggIntro from 'Components/Order/KeyReveals/Egg/EggIntro';
import EggReveal from 'Components/Order/KeyReveals/Egg/EggReveal';
import BonusDropReveal from 'Components/Order/KeyReveals/BonusDrop/BonusDropReveal';
import BonusDropIntro from 'Components/Order/KeyReveals/BonusDrop/BonusDropIntro';
import store from 'Redux/store';
import KeyReveals from 'Helpers/Order/KeyReveals';

import 'Components/Order/KeyReveals/MysteryReveal.scss';

function MysteryReveal({
  item,
  orderID,
  setOrder,
  testReveal,
  testRevealType,
  setName,
  setMysteryGameDetails,
  setMysteryRevealed,
  bundleID,
  mysteryGameDetails,
}) {
  const [revealStep, setRevealStep] = useState(1);
  const [error, setError] = useState(false);
  const [revealedOrder, setRevealedOrder] = useState({});
  const [inProgress, setInProgress] = useState(false);
  const [endFrame, setEndFrame] = useState(false);

  const uniqueId = !testReveal ? item.iid : `${Math.random()}`;

  // When revealing a whole mystery bundle, call mysteryBundleReveal and store the returned order.
  // This order is then used by the setOrder function in viewKeys at the end of the reveal process.
  // When skipping, the setOrder function can be called straight away. This will cause a re-render.

  const revealMysteryBundle = async (skip, step, isVideo) => {
    if (inProgress) {
      return false;
    }

    if (isVideo) {
      setTimeout(() => {
        setEndFrame(true);
      }, 1000);
    }

    if (!testReveal) {
      setInProgress(true);

      const newOrder = await mysteryBundleReveal(orderID, item);

      if (!isEmpty(newOrder)) {
        if (!skip) {
          setRevealedOrder(newOrder);
          setRevealStep(step);
          setInProgress(false);
        }

        if (skip) {
          setOrder(newOrder);
        }
      } else {
        setError(true);
        setInProgress(false);
      }
    }

    if (testReveal) {
      setRevealStep(step);
    }

    return true;
  };

  // When revealing an individual key, the returned data needs setting in the parent component ready
  // for the user when the reveal animation finishes.

  const revealMysteryGame = async (skip, isVideo) => {
    if (inProgress) {
      return false;
    }

    if (testReveal && isVideo) {
      setTimeout(() => {
        setEndFrame(true);
      }, 1000);
    }

    if (!testReveal) {
      setInProgress(true);

      const {
        mysteryGameDetails: newMysteryGameDetails,
        productName,
        message,
      } = await revealMysteryKey(orderID, bundleID, item);

      if (message) {
        setError(true);
        alert(message); // eslint-disable-line no-alert
        setInProgress(false);
        return false;
      }

      if (isVideo) {
        setTimeout(() => {
          setEndFrame(true);
        }, 1000);
      }

      if (productName) {
        setName(productName);
        if (skip) setMysteryRevealed(true);
        setInProgress(false);
      }

      if (newMysteryGameDetails) {
        setMysteryGameDetails(newMysteryGameDetails);
      }
    }

    setRevealStep(2);
    return true;
  };

  const skipAnimation = () => {
    if (item.type === 'game') revealMysteryGame(true);
    if (item.type === 'bundle') revealMysteryBundle(true);
    store.dispatch({ type: 'frontend/mystery-reveal/SKIP_ANIMATION', payload: { bundleID } });
  };

  const viewKeys = () => {
    if (!testReveal && !isEmpty(revealedOrder)) {
      setOrder(revealedOrder);
    }

    if (item.type === 'game') {
      setMysteryRevealed(true);
    }
  };

  let template = null;
  let IntroComponent = null;
  let bgImgSrc = '';
  let wheelImgSrc = '';
  let testGames = [];
  let testGame = {};

  if (item.reveal_template === 'mystery bundle') {
    template = 'bundle';
    IntroComponent = StandardBundleIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/scratchcard/2023/red-hot-sale-scratchcard-bg.jpg?${imgixConfig}`;
  }

  if (item.reveal_template === 'pinata' || (testReveal && testRevealType === 'pinata')) {
    template = 'pinata';
    IntroComponent = PinataIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/pinata/2024/summer-sale-pinata-background.jpg?${imgixConfig}`;
    testGames = KeyReveals.getTestPinataGames();
  }

  if (item.reveal_template === 'scratchcard' || (testReveal && testRevealType === 'scratchcard')) {
    template = 'scratchcard';
    IntroComponent = ScratchCardIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/scratchcard/2025/gaming-legends-main-background.jpg?${imgixConfig}`;
  }

  if (item.reveal_template === 'wheel' || (testReveal && testRevealType === 'wheel')) {
    template = 'wheel';
    IntroComponent = WheelIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/spin-the-wheel/2024/black-friday-background.jpg?${imgixConfig}`;
    wheelImgSrc = `https://fanatical.imgix.net/production/assets/mystery/spin-the-wheel/2024/black-friday-wheel.png?${imgixConfig}`;
  }

  if (item.reveal_template === 'wheel alt' || (testReveal && testRevealType === 'wheel alt')) {
    template = 'wheel alt';
    IntroComponent = WheelIntroJingleJam;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/spin-the-wheel/2024/jingle-jam-2024-background.jpg?${imgixConfig}`;
    wheelImgSrc = `https://fanatical.imgix.net/production/assets/mystery/spin-the-wheel/2024/jingle-jam-2024-wheel.png?${imgixConfig}`;
  }

  if (item.reveal_template === 'present' || (testReveal && testRevealType === 'present')) {
    template = 'present';
    IntroComponent = GiftBoxIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/gift-box/2024/birthday-bash-2024-background.jpg?${imgixConfig}`;
    testGame = KeyReveals.getTestGiftBoxGame();
  }

  if (item.reveal_template === 'envelope' || (testReveal && testRevealType === 'envelope')) {
    template = 'envelope';
    IntroComponent = EnvelopeIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/envelope/2025/lunar-new-year-sale-2025-envelope-background.jpg?${imgixConfig}`;
    testGame = KeyReveals.getTestEnvelopeGame();
  }

  if (item.reveal_template === 'easter egg' || (testReveal && testRevealType === 'easter egg')) {
    template = 'easter egg';
    IntroComponent = EggIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/mystery-egg/2025/first-frame.jpg?${imgixConfig}`;
    testGames = KeyReveals.getTestPinataGames();

    // Swap out background image after animation
    if (endFrame) {
      bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/mystery-egg/2025/last-frame.jpg?${imgixConfig}`;
    }
  }

  if (item.reveal_template === 'bonus drop' || (testReveal && testRevealType === 'bonus drop')) {
    template = 'bonus drop';
    IntroComponent = BonusDropIntro;
    bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/bonus-drop/2025/bonus-drop-background-first-frame.jpg?${imgixConfig}`;
    testGame = KeyReveals.getBonusDropGame();

    // Swap out background image after animation
    if (endFrame) {
      bgImgSrc = `https://fanatical.imgix.net/production/assets/mystery/bonus-drop/2025/bonus-drop-background-last-frame.jpg?${imgixConfig}`;
    }
  }

  if (template === null) return false;

  const videoBackground = false;
  const preloadWheel = item.reveal_template === 'wheel' || item.reveal_template === 'wheel alt' || (testReveal && (testRevealType === 'wheel' || testRevealType === 'wheel alt'));
  const preloadVideo = template === 'easter egg' || template === 'bonus drop';

  return (
    <div className={`MysteryReveal MysteryReveal--step${revealStep} MysteryReveal--${template.replace(/\s/g, '')}`}>
      <div className="MysteryReveal__content">
        {revealStep === 1 &&
          <IntroComponent
            revealMysteryBundle={revealMysteryBundle}
            revealMysteryGame={revealMysteryGame}
            error={error}
            skipAnimation={skipAnimation}
            testReveal={testReveal}
            smallprint={item.order_smallprint}
            itemType={item.type}
          />
        }

        {revealStep === 2 &&
          <React.Fragment>
            {template === 'pinata' &&
              <PinataReveal
                setRevealStep={setRevealStep}
                testReveal={testReveal}
                uniqueId={uniqueId}
                smallprint={item.order_smallprint}
              />
            }
            {template === 'wheel' &&
              <WheelReveal
                item={item}
                mysteryGameDetails={mysteryGameDetails}
                testReveal={testReveal}
                uniqueId={uniqueId}
                viewKeys={viewKeys}
                smallprint={item.order_smallprint}
                wheelImgSrc={wheelImgSrc}
              />
            }
            {template === 'wheel alt' &&
              <WheelRevealJingleJam
                item={item}
                mysteryGameDetails={mysteryGameDetails}
                testReveal={testReveal}
                uniqueId={uniqueId}
                viewKeys={viewKeys}
                smallprint={item.order_smallprint}
                wheelImgSrc={wheelImgSrc}
              />
            }
            {template === 'scratchcard' &&
              <ScratchCardReveal
                item={item}
                mysteryGameDetails={mysteryGameDetails}
                testReveal={testReveal}
                uniqueId={uniqueId}
                viewKeys={viewKeys}
                smallprint={item.order_smallprint}
              />
            }
            {template === 'present' &&
              <GiftBoxReveal
                item={item}
                testReveal={testReveal}
                uniqueId={uniqueId}
                viewKeys={viewKeys}
                smallprint={item.order_smallprint}
                testGame={testGame}
              />
            }
            {template === 'envelope' &&
              <EnvelopeReveal
                item={item}
                testReveal={testReveal}
                uniqueId={uniqueId}
                viewKeys={viewKeys}
                smallprint={item.order_smallprint}
                testGame={testGame}
              />
            }
            {template === 'easter egg' &&
              <EggReveal
                setRevealStep={setRevealStep}
                testReveal={testReveal}
                uniqueId={uniqueId}
                smallprint={item.order_smallprint}
              />
            }
            {template === 'bonus drop' &&
              <BonusDropReveal
                item={item}
                testReveal={testReveal}
                uniqueId={uniqueId}
                viewKeys={viewKeys}
                smallprint={item.order_smallprint}
                testGame={testGame}
              />
            }
          </React.Fragment>
        }

        {revealStep === 3 &&
          <MysteryRevealGames
            item={item}
            revealedOrder={revealedOrder}
            viewKeys={viewKeys}
            template={template}
            testReveal={testReveal}
            testGames={testGames}
          />
        }

        {videoBackground &&
          <div className="MysteryReveal__videoBackground">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="hero-background hero-video"
            >
              <source src="https://cdn.fanatical.com/production/assets/mystery/scratchcard/2023/red-hot-sale-scratchcard-bg-video.mp4" type="video/mp4" />
              <source src="https://cdn.fanatical.com/production/assets/mystery/scratchcard/2023/red-hot-sale-scratchcard-bg-video.webm" type="video/webm" />
            </video>
          </div>
        }

        {preloadWheel &&
          <div className="MysteryReveal__hiddenWheel">
            <img
              src={wheelImgSrc}
              alt="Spin To Win Wheel"
              className="wheel"
            />
          </div>
        }

        {preloadVideo &&
          <div className="MysteryReveal__hiddenWheel">
            <video
              muted
              className="hero-background hero-video"
            >
              {template === 'easter egg' &&
                <source src="https://cdn.fanatical.com/production/assets/mystery/mystery-egg/2025/mystery-egg-video.mp4" type="video/mp4" />
              }
              {template === 'bonus drop' &&
                <source src="https://cdn.fanatical.com/production/assets/mystery/bonus-drop/2025/bonus-drop-video.mp4" type="video/mp4" />
              }
            </video>
          </div>
        }

        <div className="MysteryReveal__background">
          <img
            srcSet={`
              ${bgImgSrc}?${imgixConfig}&w=1920&h=1080&fit=crop 1920w,
              ${bgImgSrc}?${imgixConfig}&w=1280&h=720&fit=crop 1280w,
              ${bgImgSrc}?${imgixConfig}&w=1130&h=636&fit=crop 1130w,
              ${bgImgSrc}?${imgixConfig}&w=960&h=540&fit=crop 960w,
              ${bgImgSrc}?${imgixConfig}&w=740&h=416&fit=crop 740w,
              ${bgImgSrc}?${imgixConfig}&w=640&h=360&fit=crop 640w,
              ${bgImgSrc}?${imgixConfig}&w=480&h=270&fit=crop 480w,
              ${bgImgSrc}?${imgixConfig}&w=384&h=216&fit=crop 384w
            `}
            sizes="100vw"
            src={`${bgImgSrc}?${imgixConfig}&w=1920&h=1080&fit=crop`}
            alt="Background"
          />
        </div>
      </div>
    </div>
  );
}

MysteryReveal.propTypes = {
  item: PropTypes.shape({}).isRequired,
  orderID: PropTypes.string,
  bundleID: PropTypes.string,
  setMysteryGameDetails: PropTypes.func,
  setMysteryRevealed: PropTypes.func,
  setName: PropTypes.func,
  setOrder: PropTypes.func,
  testReveal: PropTypes.bool,
  testRevealType: PropTypes.string,
};

MysteryReveal.defaultProps = {
  item: {},
  orderID: '',
  bundleID: null,
  setMysteryGameDetails: () => {},
  setMysteryRevealed: () => {},
  setName: () => {},
  setOrder: () => {},
  testReveal: false,
  testRevealType: null,
};

const mapStateToProps = ({
  router: { location },
}) => ({
  location,
});

export default connect(mapStateToProps)(MysteryReveal);
